import { useState, useEffect } from 'react';
import api from '../api/axios';
import { formatDate } from '../utils/dateUtils';

export default function Assistant() {
  const [activeTab, setActiveTab] = useState('coach');
  const [tasks, setTasks] = useState([]);
  const [goals, setGoals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [openMenuId, setOpenMenuId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (filterStatus || filterPriority) {
      fetchData();
    }
  }, [filterStatus, filterPriority]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Build query params for tasks
      const params = new URLSearchParams();
      if (filterStatus && filterStatus !== 'all') {
        params.append('status', filterStatus);
      }
      if (filterPriority && filterPriority !== 'all') {
        params.append('priority', filterPriority);
      }

      const tasksUrl = `/assistant/tasks${params.toString() ? `?${params.toString()}` : ''}`;

      const [tasksRes, goalsRes, categoriesRes] = await Promise.all([
        api.get(tasksUrl),
        api.get('/assistant/goals'),
        api.get('/assistant/categories')
      ]);

      setTasks(tasksRes.data.tasks || []);
      setGoals(goalsRes.data.goals || []);
      setCategories(categoriesRes.data.categories || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async (taskData) => {
    try {
      // Convert empty strings to null for database
      const cleanedData = {
        ...taskData,
        category_id: taskData.category_id === '' ? null : parseInt(taskData.category_id),
        due_date: taskData.due_date === '' ? null : taskData.due_date
      };
      await api.post('/assistant/tasks', cleanedData);
      await fetchData();
      setShowTaskModal(false);
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Failed to create task');
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      // Convert empty strings to null for database
      const cleanedData = {
        ...updates,
        category_id: updates.category_id === '' || updates.category_id === undefined ? null : parseInt(updates.category_id),
        due_date: updates.due_date === '' || updates.due_date === undefined ? null : updates.due_date
      };
      await api.put(`/assistant/tasks/${taskId}`, cleanedData);
      await fetchData();
      setEditingTask(null);
    } catch (error) {
      console.error('Error updating task:', error);
      alert('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      await api.delete(`/assistant/tasks/${taskId}`);
      await fetchData();
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task');
    }
  };

  const handleCreateGoal = async (goalData) => {
    try {
      await api.post('/assistant/goals', goalData);
      await fetchData();
      setShowGoalModal(false);
    } catch (error) {
      console.error('Error creating goal:', error);
      alert('Failed to create goal');
    }
  };

  const handleUpdateGoal = async (goalId, updates) => {
    try {
      await api.put(`/assistant/goals/${goalId}`, updates);
      await fetchData();
    } catch (error) {
      console.error('Error updating goal:', error);
      alert('Failed to update goal');
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800'
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.pending;
  };

  const filteredTasks = tasks.filter(task => {
    if (filterStatus !== 'all' && task.status !== filterStatus) return false;
    if (filterPriority !== 'all' && task.priority !== filterPriority) return false;
    return true;
  });

  const dailyGoals = goals.filter(g => g.goal_type === 'daily');
  const weeklyGoals = goals.filter(g => g.goal_type === 'weekly');
  const monthlyGoals = goals.filter(g => g.goal_type === 'monthly');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Virtual Assistant</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage your tasks, goals, and stay organized
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowTaskModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            + New Task
          </button>
          <button
            onClick={() => setShowGoalModal(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            + New Goal
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {['coach', 'tasks', 'daily', 'weekly', 'monthly', 'analytics'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab === 'coach' ? '🎯 Daily Coach' : tab === 'tasks' ? 'All Tasks' : `${tab} Goals`}
            </button>
          ))}
        </nav>
      </div>

      {/* Coach Tab */}
      {activeTab === 'coach' && (
        <CoachView tasks={tasks} goals={goals} />
      )}

      {/* Tasks Tab */}
      {activeTab === 'tasks' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2"
            >
              <option value="all">All Priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          {/* Tasks List */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTasks.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      No tasks found. Create your first task to get started!
                    </td>
                  </tr>
                ) : (
                  filteredTasks.map(task => (
                    <tr key={task.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">{task.title}</div>
                          {task.description && (
                            <div className="text-sm text-gray-500 mt-1">{task.description.substring(0, 60)}{task.description.length > 60 ? '...' : ''}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {task.category_name && (
                          <span className="inline-flex items-center gap-1">
                            <span>{task.category_icon}</span>
                            <span className="text-sm">{task.category_name}</span>
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={task.status}
                          onChange={(e) => handleUpdateTask(task.id, { status: e.target.value })}
                          className={`px-2 py-1 text-xs font-semibold rounded-full border-0 ${getStatusColor(task.status)}`}
                        >
                          <option value="pending">Pending</option>
                          <option value="in_progress">In Progress</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDate(task.due_date)}
                      </td>
                      <td className="px-6 py-4 relative">
                        <button
                          onClick={() => setOpenMenuId(openMenuId === task.id ? null : task.id)}
                          className="text-gray-600 hover:text-gray-900 p-1 rounded hover:bg-gray-100"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                          </svg>
                        </button>
                        {openMenuId === task.id && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setOpenMenuId(null)}
                            />
                            <div className="absolute right-0 bottom-full mb-2 w-48 bg-white rounded-md shadow-xl z-20 border border-gray-200 overflow-hidden">
                              <button
                                onClick={() => {
                                  setEditingTask(task);
                                  setOpenMenuId(null);
                                }}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                              >
                                Edit Task
                              </button>
                              <button
                                onClick={() => {
                                  handleDeleteTask(task.id);
                                  setOpenMenuId(null);
                                }}
                                className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors border-t border-gray-100"
                              >
                                Delete Task
                              </button>
                            </div>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Daily Goals Tab */}
      {activeTab === 'daily' && (
        <GoalsList
          goals={dailyGoals}
          type="daily"
          onUpdate={handleUpdateGoal}
        />
      )}

      {/* Weekly Goals Tab */}
      {activeTab === 'weekly' && (
        <GoalsList
          goals={weeklyGoals}
          type="weekly"
          onUpdate={handleUpdateGoal}
        />
      )}

      {/* Monthly Goals Tab */}
      {activeTab === 'monthly' && (
        <GoalsList
          goals={monthlyGoals}
          type="monthly"
          onUpdate={handleUpdateGoal}
        />
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <AnalyticsView tasks={tasks} goals={goals} />
      )}

      {/* Task Modal */}
      {showTaskModal && (
        <TaskModal
          categories={categories}
          onSave={handleCreateTask}
          onClose={() => setShowTaskModal(false)}
        />
      )}

      {/* Edit Task Modal */}
      {editingTask && (
        <TaskModal
          categories={categories}
          task={editingTask}
          onSave={(taskData) => handleUpdateTask(editingTask.id, taskData)}
          onClose={() => setEditingTask(null)}
          onDelete={() => {
            handleDeleteTask(editingTask.id);
            setEditingTask(null);
          }}
          isEditing={true}
        />
      )}

      {/* Goal Modal */}
      {showGoalModal && (
        <GoalModal
          onSave={handleCreateGoal}
          onClose={() => setShowGoalModal(false)}
        />
      )}
    </div>
  );
}

// Coach Component
function CoachView({ tasks, goals }) {
  const today = new Date();
  const dayOfWeek = today.toLocaleDateString('en-US', { weekday: 'long' });

  // Analytics calculations
  const overdueTasks = tasks.filter(t =>
    t.due_date && new Date(t.due_date) < today && t.status !== 'completed'
  );
  const todayTasks = tasks.filter(t => {
    if (!t.due_date) return false;
    const dueDate = new Date(t.due_date);
    return dueDate.toDateString() === today.toDateString() && t.status !== 'completed';
  });
  const urgentTasks = tasks.filter(t =>
    t.priority === 'urgent' && t.status !== 'completed'
  );
  const inProgressTasks = tasks.filter(t => t.status === 'in_progress');

  const activeGoals = goals.filter(g => g.status === 'active');
  const todayGoals = goals.filter(g => g.goal_type === 'daily' && g.status === 'active');
  const lowProgressGoals = activeGoals.filter(g => g.progress < 50);

  // Generate recommendations
  const recommendations = [];

  if (overdueTasks.length > 0) {
    recommendations.push({
      priority: 'urgent',
      icon: '🚨',
      title: 'Address Overdue Tasks',
      message: `You have ${overdueTasks.length} overdue task${overdueTasks.length > 1 ? 's' : ''}. Focus on these first to get back on track.`,
      action: 'View overdue tasks',
      tasks: overdueTasks.slice(0, 3)
    });
  }

  if (urgentTasks.length > 0) {
    recommendations.push({
      priority: 'high',
      icon: '⚡',
      title: 'Urgent Priorities',
      message: `${urgentTasks.length} urgent task${urgentTasks.length > 1 ? 's need' : ' needs'} your immediate attention.`,
      action: 'Focus on urgent tasks',
      tasks: urgentTasks.slice(0, 3)
    });
  }

  if (todayTasks.length > 0) {
    recommendations.push({
      priority: 'medium',
      icon: '📅',
      title: 'Today\'s Agenda',
      message: `You have ${todayTasks.length} task${todayTasks.length > 1 ? 's' : ''} due today. Let's make progress!`,
      action: 'View today\'s tasks',
      tasks: todayTasks
    });
  }

  if (todayGoals.length > 0) {
    const incomplete = todayGoals.filter(g => g.progress < 100);
    if (incomplete.length > 0) {
      recommendations.push({
        priority: 'medium',
        icon: '🎯',
        title: 'Daily Goals',
        message: `Make progress on your ${incomplete.length} active daily goal${incomplete.length > 1 ? 's' : ''}.`,
        action: 'Update goals',
        goals: incomplete
      });
    }
  }

  if (inProgressTasks.length > 0) {
    recommendations.push({
      priority: 'low',
      icon: '🔄',
      title: 'Continue Working Tasks',
      message: `You have ${inProgressTasks.length} task${inProgressTasks.length > 1 ? 's' : ''} in progress. Keep the momentum going!`,
      action: 'View in-progress tasks',
      tasks: inProgressTasks.slice(0, 3)
    });
  }

  if (lowProgressGoals.length > 0) {
    recommendations.push({
      priority: 'low',
      icon: '📊',
      title: 'Goal Progress Check',
      message: `${lowProgressGoals.length} goal${lowProgressGoals.length > 1 ? 's are' : ' is'} below 50% progress. Consider breaking them into smaller tasks.`,
      action: 'Review goals',
      goals: lowProgressGoals.slice(0, 3)
    });
  }

  if (recommendations.length === 0) {
    recommendations.push({
      priority: 'success',
      icon: '✨',
      title: 'You\'re All Caught Up!',
      message: 'Great work! All your tasks are on track. Consider setting new goals or planning ahead.',
      action: 'Create new task'
    });
  }

  const getPriorityStyle = (priority) => {
    switch(priority) {
      case 'urgent': return 'border-l-4 border-red-500 bg-red-50';
      case 'high': return 'border-l-4 border-orange-500 bg-orange-50';
      case 'medium': return 'border-l-4 border-blue-500 bg-blue-50';
      case 'success': return 'border-l-4 border-green-500 bg-green-50';
      default: return 'border-l-4 border-gray-500 bg-gray-50';
    }
  };

  return (
    <div className="space-y-6">
      {/* Greeting Card */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-lg p-8 text-white">
        <h2 className="text-3xl font-bold mb-2">Good {
          today.getHours() < 12 ? 'Morning' : today.getHours() < 18 ? 'Afternoon' : 'Evening'
        }! ☀️</h2>
        <p className="text-blue-100 text-lg">
          It's {dayOfWeek}, {today.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
        <p className="mt-4 text-lg">
          Let's review what needs your attention today and create a winning game plan! 🎯
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-red-600">{overdueTasks.length}</div>
          <div className="text-sm text-gray-600">Overdue</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-blue-600">{todayTasks.length}</div>
          <div className="text-sm text-gray-600">Due Today</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-orange-600">{urgentTasks.length}</div>
          <div className="text-sm text-gray-600">Urgent</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-green-600">{activeGoals.length}</div>
          <div className="text-sm text-gray-600">Active Goals</div>
        </div>
      </div>

      {/* Coaching Recommendations */}
      <div className="space-y-4">
        <h3 className="text-2xl font-bold text-gray-900">Today's Focus Areas</h3>
        {recommendations.map((rec, index) => (
          <div key={index} className={`rounded-lg shadow p-6 ${getPriorityStyle(rec.priority)}`}>
            <div className="flex items-start gap-4">
              <div className="text-4xl">{rec.icon}</div>
              <div className="flex-1">
                <h4 className="text-xl font-bold text-gray-900 mb-2">{rec.title}</h4>
                <p className="text-gray-700 mb-4">{rec.message}</p>

                {/* Show related tasks */}
                {rec.tasks && rec.tasks.length > 0 && (
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <div className="text-sm font-semibold text-gray-700 mb-2">Tasks to focus on:</div>
                    <ul className="space-y-2">
                      {rec.tasks.map(task => (
                        <li key={task.id} className="flex items-center gap-2 text-sm">
                          <span className={`px-2 py-0.5 text-xs rounded ${
                            task.priority === 'urgent' ? 'bg-red-200 text-red-800' :
                            task.priority === 'high' ? 'bg-orange-200 text-orange-800' :
                            'bg-blue-200 text-blue-800'
                          }`}>
                            {task.priority}
                          </span>
                          <span className="font-medium">{task.title}</span>
                          {task.due_date && (
                            <span className="text-gray-500 text-xs">
                              • Due: {formatDate(task.due_date)}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Show related goals */}
                {rec.goals && rec.goals.length > 0 && (
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <div className="text-sm font-semibold text-gray-700 mb-2">Goals needing attention:</div>
                    <ul className="space-y-2">
                      {rec.goals.map(goal => (
                        <li key={goal.id} className="text-sm">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium">{goal.title}</span>
                            <span className="text-gray-600">{goal.progress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${goal.progress}%` }}
                            ></div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Motivational Quote */}
      <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg shadow-lg p-6 text-white text-center">
        <p className="text-xl italic mb-2">
          "Success is the sum of small efforts repeated day in and day out."
        </p>
        <p className="text-purple-200">— Robert Collier</p>
      </div>
    </div>
  );
}

// Goals List Component
function GoalsList({ goals, type, onUpdate }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {goals.length === 0 ? (
        <div className="col-span-full text-center py-12 text-gray-500">
          No {type} goals yet. Create one to get started!
        </div>
      ) : (
        goals.map(goal => (
          <div key={goal.id} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-semibold text-lg text-gray-900">{goal.title}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Due: {formatDate(goal.target_date)}
                </p>
              </div>
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                goal.status === 'completed' ? 'bg-green-100 text-green-800' :
                goal.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                {goal.status}
              </span>
            </div>

            {goal.description && (
              <p className="text-sm text-gray-600 mb-4">{goal.description}</p>
            )}

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-gray-600">Progress</span>
                <span className="font-semibold">{goal.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${goal.progress}%` }}
                ></div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="range"
                min="0"
                max="100"
                value={goal.progress}
                onChange={(e) => onUpdate(goal.id, { progress: parseInt(e.target.value) })}
                className="flex-1"
              />
              {goal.status !== 'completed' && goal.progress === 100 && (
                <button
                  onClick={() => onUpdate(goal.id, { status: 'completed' })}
                  className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                >
                  Mark Complete
                </button>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// Analytics Component
function AnalyticsView({ tasks, goals }) {
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const inProgressTasks = tasks.filter(t => t.status === 'in_progress').length;
  const pendingTasks = tasks.filter(t => t.status === 'pending').length;
  const overdueTasks = tasks.filter(t =>
    t.due_date && new Date(t.due_date) < new Date() && t.status !== 'completed'
  ).length;

  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-600">Total Tasks</div>
          <div className="text-3xl font-bold text-gray-900 mt-2">{totalTasks}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-600">Completed</div>
          <div className="text-3xl font-bold text-green-600 mt-2">{completedTasks}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-600">In Progress</div>
          <div className="text-3xl font-bold text-blue-600 mt-2">{inProgressTasks}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-600">Overdue</div>
          <div className="text-3xl font-bold text-red-600 mt-2">{overdueTasks}</div>
        </div>
      </div>

      {/* Completion Rate */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Task Completion Rate</h3>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div
                className="bg-green-600 h-4 rounded-full transition-all"
                style={{ width: `${completionRate}%` }}
              ></div>
            </div>
          </div>
          <div className="text-2xl font-bold text-green-600">{completionRate}%</div>
        </div>
      </div>

      {/* Goals Overview */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Goals Overview</h3>
        <div className="space-y-3">
          {['daily', 'weekly', 'monthly'].map(type => {
            const typeGoals = goals.filter(g => g.goal_type === type);
            const completedGoals = typeGoals.filter(g => g.status === 'completed').length;
            return (
              <div key={type} className="flex items-center justify-between">
                <span className="capitalize font-medium">{type} Goals</span>
                <span className="text-gray-600">
                  {completedGoals} / {typeGoals.length} completed
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Task Modal Component
function TaskModal({ categories, task, onSave, onClose, onDelete, isEditing = false }) {
  const [formData, setFormData] = useState({
    title: task?.title || '',
    description: task?.description || '',
    category_id: task?.category_id || '',
    priority: task?.priority || 'medium',
    due_date: task?.due_date || '',
    status: task?.status || 'pending'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h2 className="text-2xl font-bold mb-4">
          {isEditing ? 'Edit Task' : 'Create New Task'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title*</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              rows="3"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={formData.category_id}
              onChange={(e) => setFormData({...formData, category_id: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">No category</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({...formData, priority: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          {isEditing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
            <input
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData({...formData, due_date: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div className="flex gap-3 pt-4">
            {isEditing && onDelete && (
              <button
                type="button"
                onClick={onDelete}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            )}
            <button
              type="submit"
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              {isEditing ? 'Update Task' : 'Create Task'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Goal Modal Component
function GoalModal({ onSave, onClose }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    goal_type: 'daily',
    target_date: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h2 className="text-2xl font-bold mb-4">Create New Goal</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title*</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              rows="3"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Goal Type*</label>
            <select
              value={formData.goal_type}
              onChange={(e) => setFormData({...formData, goal_type: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Target Date*</label>
            <input
              type="date"
              required
              value={formData.target_date}
              onChange={(e) => setFormData({...formData, target_date: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              Create Goal
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
